import { Component, OnInit, Input, Output, EventEmitter, ViewChild, AfterViewInit, TemplateRef, ContentChildren, Directive, AfterContentInit, ViewChildren, ElementRef } from '@angular/core';
import { GridSpinnerComponent } from '../grid-spinner.component';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ResultFilter, Results, PlatformApiService } from 'src/app/services/platform-api.service';
import { DatePipe } from '@angular/common';
import { FormGroup, FormControl } from '@angular/forms';
import * as moment from 'moment';
import { Metric } from 'src/app/models/MetricModel';
import { logging } from 'protractor';

/**
 * This component defines a reusable data tabel for the Identify application. The calling component needs the following for this to work:
 *
 * In the template:
 *
 *
 * <cw-ci-data-table #boundObject
 *   tileTitle="TITLE"
 *   addToolTip="ADD ACTION TOOLTIP"
 *   refreshToolTip="RELOAD LIST TOOLTIP"
 *   defaultSortField="DEFAULT SORTING FIELD"
 *   [isSelectable]="isSelectable"
 *   [isActive]="isActive"
 *   (loadItemsHandler)="loadItems($event)"
 *   (addHandler)="newItem($event)"
 *   (selectHandler)="selectItem($event)"
 *  [items]="dataTableItems">
 *
 *   <!-- specifies a column with custom output. Note that [output] must equal the template reference that renders the value -->
 *   <cw-ci-table-column columnName="active" columnTitle="Active" [output]="activeTemplate"></cw-ci-table-column>
 *   <!-- Note that [let-element] is required and element will refer to the item of the row being rendered -->
 *   <ng-template let-element #activeTemplate><mat-icon color="accent" *ngIf="element.isActive">check</mat-icon></ng-template>
 *   <!-- specifies a column that simply displays the value of the property specified by [columnName] -->
 *   <cw-ci-table-column columnName="name" columnTitle="Account Name" sortField="account_name"></cw-ci-table-column>
 *   <!-- shows how actions buttons can be added -->
 *   <cw-ci-table-column columnName="actions" columnTitle="Actions" [output]="actionTemplate"></cw-ci-table-column>
 *   <ng-template let-element #actionTemplate>
 *     <cw-ci-table-column-action [disabled]="!element.isActive" actionToolTip="Edit the Account" svgIcon="cw:action_edit" actionColor="primary" [element]="element" (clickHandler)="editAccount($event)"></cw-ci-table-column-action>
 *     <cw-ci-table-column-action [visible]="element.type != 'SYSTEM' && element.isActive" actionToolTip="Deactivate the Account" matIcon="delete" actionColor="primary" [element]="element" (clickHandler)="deleteAccount($event)"></cw-ci-table-column-action>
 *     <cw-ci-table-column-action [visible]="element.type != 'SYSTEM' && !element.isActive" actionToolTip="Delete the Account" matIcon="delete_forever" actionColor="warn" [element]="element" (clickHandler)="deleteAccount($event)"></cw-ci-table-column-action>
 *   </ng-template>
 * </cw-ci-data-table>
 *   In the component, the following properties must be defined:
 *     // This allows the calling component to interact with the table programatically
 *     @ViewChild('boundObject') boundObject: TileDataTableComponent;
 *     // This is an array of the objects type show in the table.
 *     dataTableItems: ObjectType[];
 *     // This function determines whether the object should be selectable within the table.
 *     isSelectable(obj) {
 *       return true;
 *     }
 *     // This function populates the dataTableItems object based on the passed in filter
 *     loadItems(filter: ResultFilter) {
 *     }
 *     // This function allows the caller to add a new item
 *     newItem() {
 *     }
 *     // This function allows the caller to select an item from the table. It is passed in the selected item
 *     selectItem(item) {
 *     }
 */

@Component({
  selector: 'cw-ci-table-column',
  template: ''
})
export class DataTableColumnComponent {
  @Input() columnName;
  @Input() columnTitle;
  @Input() columnType;
  @Input() sortField;
  @Input() searchField;
  @Input() output: TemplateRef<any>;
  @Input() filterType: string;
  @Input() filterOptions: string[] = [];
  @Input() maxWidth: string;
  @Input() minWidth: string;
}

@Component({
  selector: 'cw-ci-table-column-action',
  template: `
    <button *ngIf="visible" [matTooltip]="actionToolTip" matTooltipPosition="left" [color]="actionColor" mat-icon-button (click)="triggerClick($event)" [disabled]="disabled">
      <mat-icon [svgIcon]="svgIcon">{{matIcon}}</mat-icon><ng-content></ng-content>
    </button>
  `
})
export class DataTableColumnActionComponent {
  @Input() actionToolTip;
  @Input() svgIcon;
  @Input() matIcon;
  @Input() actionColor;
  @Input() element;
  @Input() disabled = false;
  @Input() visible = true;
  @Output() clickHandler: EventEmitter<any> = new EventEmitter();

  triggerClick(event) {
    event.stopPropagation();
    this.clickHandler.emit(this.element);
  }
}

@Component({
  selector: 'cw-ci-data-table',
  templateUrl: './tile-data-table.component.html',
  styleUrls: ['./tile-data-table.component.scss'],
  providers: [DatePipe]
})
export class DataTableComponent implements OnInit, AfterContentInit, AfterViewInit {

  @ContentChildren(DataTableColumnComponent) columnDefns;
  @Input() tileTitle: string;
  @Input() tableId: string;
  @Input() addToolTip: string;
  @Input() refreshToolTip: string;
  @Input() hidePageSize: boolean;
  @Input() hideRefreshButton: boolean;
  @Input() hideWrapper:boolean;
  @Input() showLoadingAlert = true;

  @Input() defaultSortField: string;
  @Input() defaultSortDirection: SortDirection = 'asc';
  @Input() isSelectable;

  @Output() loadItemsHandler: EventEmitter<any> = new EventEmitter();
  @Output() addHandler: EventEmitter<any> = new EventEmitter();
  @Output() selectHandler: EventEmitter<any> = new EventEmitter();
  @Input() emptyListOverlay: TemplateRef<any>;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('spinner', { static: true }) spinner: GridSpinnerComponent;
  @ViewChild('emptyOverlayElement') emptyOverlayElement: ElementRef;

  showEmptyListOverlay = false;

  selectedItem: any;
  resultsLength: number;
  isLoading = true;
  _tableItems: {} | [];
  _pageSize = 50;
  _columnsToDisplay: string[] = [];
  _filterColumnsToDisplay: string[] = [];
  hasAddObserver = false;
  hasSelectObserver = false;
  resetPagination = false;
  pageNumbers: number[];
  goTo: number;
  pageIndex: number;
  @Output() page = new EventEmitter<PageEvent>();

  @Input('pageSize') set pageSizeChanged(ps:number) {
    this._pageSize = ps;
    this.updateGoto();
  }
  @Input('pageIndex') set pageIndexChanged(pageIndex: number) {
    this.pageIndex = pageIndex;
  };

  filterForm = new FormGroup ({});
  isFilteredSearch = false;

  @Input() showAdd = true;
  @Input() skipInitialLoad:boolean;
  @Input() disableAdd = false;

  @Input() isActive = (item) => true;
  searchStartTime: any;

  constructor(private datePipe: DatePipe, private platformApi: PlatformApiService) { }

  ngOnInit() {
    this.hasAddObserver = this.addHandler.observers.length > 0;
    this.hasSelectObserver = this.selectHandler.observers.length > 0;
    if(!this.skipInitialLoad){
      this.executeLoadItems();
    }
  }

  ngAfterContentInit() {
    this.initializeTableColumns();
  }

  resetDataTable() {
    this.filterForm = new FormGroup({});
    this.initializeTableColumns();
  }

  private initializeTableColumns() {
    this._columnsToDisplay = this.columnDefns.map(el => el.columnName);
    this._filterColumnsToDisplay = this.columnDefns.map(el => {
      const filterColumn = el.columnName + '_filter';

      if (el.filterType != undefined) {
        const control = new FormControl('');

        control.valueChanges.pipe(debounceTime(400)).subscribe(val => {
          this.resetPagination = true;
          this.isFilteredSearch = true;
          this.executeLoadItems();
        })

        const sf = el.searchField ? el.searchField : el.columnName;
        this.filterForm.addControl(sf, control);
      }
      return filterColumn;
    });
   }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

    merge(this.sort.sortChange, this.paginator.page).subscribe(() => {
      this.executeLoadItems();
    });
  }

  @Input()
  set items(data:Results) {
    if (data) {
      if(this.isLoading && this.searchStartTime && this.tableId) {
        let endTime = moment();
        let timediff = endTime.diff(this.searchStartTime, 'seconds', true);
        this.platformApi.saveMetric(new Metric({key: this.tableId + "-loadTime", numberOfSeconds: timediff})).subscribe(resp => {});
        console.log(`Multiline took ${timediff} seconds to load`);
        this.searchStartTime = undefined;
      }
      this.isLoading = false;
      if (this.showLoadingAlert) {
        this.spinner.hideSpinner();
      }
      this.resultsLength = data.count;
      this._tableItems = data.results;
      if (this.resetPagination) {
        this.paginator.pageIndex = 0;
        this.resetPagination = false;
      }

      if (this.resultsLength == 0 && !this.isFilteredSearch) {
        this.showEmptyListOverlay = true;
      } else if(this.resultsLength > 0) {
        this.showEmptyListOverlay = false;
        this.updateGoto();
      }
    }
  }

  updateGoto() {
    this.goTo = (this.pageIndex || 0) + 1;
    this.pageNumbers = [];
    for (let i = 0; i < this.resultsLength / this._pageSize; i++) {
      const a = i+1
      this.pageNumbers.push(a);
    }
  }

  goToPage() {
    this.paginator.pageIndex = this.goTo - 1;
    this.paginator.page.next({
      length: this.paginator.length,
      pageIndex: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize
    });
  }

  paginationChange(pageEvt: PageEvent) {
    this.resultsLength = pageEvt.length;
    this.pageIndex = pageEvt.pageIndex;
    this._pageSize = pageEvt.pageSize;
    this.updateGoto();
  }

  calculateHeight() {
    if (this.showEmptyListOverlay && this.emptyOverlayElement) {
      const ht = this.emptyOverlayElement.nativeElement.clientHeight + 100;
      return {height:`${ht}px`};
    } else {
      return {};
    }
  }

  showFilter() {
    return Object.keys(this.filterForm.controls).length > 0;
  }

  executeLoadItems() {
    this.showProcessing();
    this.searchStartTime = moment();
    this.loadItemsHandler.emit(this.getResultFilter());
  }

  executeAddItem() {
    this.addHandler.emit();
  }

  checkSelectable(item) {
    if (this.hasSelectObserver) {
      return this.isSelectable(item);
    } else {
      return false;
    }
  }

  checkActive(item) {
    return this.isActive(item);
  }

  executeSelectItem(event, selectedItem: any) {
    event.stopPropagation();
    if (this.hasSelectObserver && this.isSelectable(selectedItem)) {
      this.selectHandler.emit(selectedItem);
    }
  }

  getResultFilter() {
    const searchData = {};

    Object.keys(this.filterForm.controls).forEach(k => {
      searchData[k] = this.filterForm.controls[k].value.trim();
    });

    return new ResultFilter(
      this.sort.active || this.defaultSortField,
      this.sort.direction || this.defaultSortDirection,
      this.paginator.pageSize || this._pageSize,
      this.paginator.pageIndex * (this.paginator.pageSize || this._pageSize),
      searchData);
  }

  showProcessing(message?: string, timeoutMs?: number) {
    this.isLoading = true;
    if (this.showLoadingAlert) {
      this.showEmptyListOverlay = false;
      if (message) {
        this.spinner.showMessage(message, timeoutMs);
      } else {
        this.spinner.showSpinner();
      }
    }
  }

  hideProcessing() {
    if (this.showLoadingAlert) {
      this.spinner.hideSpinner();
    }
  }

  getProperty(obj, objProperty, propertyType?: string) {
    let val = getDeepProperty(objProperty.split('.'), obj);
    if (propertyType == 'date') {
      val = this.datePipe.transform(val);
    }

    return val;
  }

  onChange(event) {
    console.log(event);
  }
}

const getDeepProperty = (p, o) => p.reduce((xs, x) => (xs && xs[x]) ? xs[x] : null, o)